import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import {
  BookOpen, Check, ChevronRight, Target,
  CheckCircle2, Brain, Clock, Award
} from "lucide-react";

import SEO from "../../components/SEO";

const ChapterPracticePage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const chapters = [
    { id: "chapter_1", title: "Applying for Citizenship", questions: 11 },
    { id: "chapter_2", title: "Rights and Responsibilities", questions: 49 },
    { id: "chapter_3", title: "Who We Are", questions: 48 },
    { id: "chapter_4", title: "Canada's History", questions: 105 },
    { id: "chapter_5", title: "Modern Canada", questions: 18 },
    { id: "chapter_6", title: "How Canadians Govern Themselves", questions: 83 },
    { id: "chapter_7", title: "Federal Elections", questions: 23 },
    { id: "chapter_8", title: "The Justice System", questions: 7 },
    { id: "chapter_9", title: "Canadian Symbols", questions: 27 },
    { id: "chapter_10", title: "Canada's Economy", questions: 30 },
    { id: "chapter_11", title: "Canada's Regions", questions: 99 },
  ];

  const benefits = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Focused Learning",
      description: "Master one topic at a time with chapter-specific questions covering all 11 chapters of Discover Canada."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Detailed Explanations",
      description: "Every question includes comprehensive explanations to help you understand why each answer is correct."
    },
    {
      icon: <CheckCircle2 className="w-6 h-6" />,
      title: "Track Your Progress",
      description: "See which chapters you've mastered and which ones need more attention with our progress tracking."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Learn at Your Pace",
      description: "No time limits on chapter practice—take your time to truly understand each concept."
    }
  ];

  return (
    <>
      <SEO
        title="Chapter Practice — 500+ Questions Covering All 11 Chapters of Discover Canada"
        description="Practice Canadian citizenship test questions chapter by chapter. 500+ questions covering all 11 chapters of Discover Canada with detailed explanations. Chapters 1-2 free. Master each topic before your exam."
        keywords="Canadian citizenship test questions, Discover Canada chapters, citizenship practice questions, chapter practice test, Canadian history questions, government of Canada test, citizenship exam chapters"
        canonical="https://canadatestprep.ca/features/chapter-practice"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Features" },
          { name: "Chapter Practice", url: "/features/chapter-practice" }
        ]}
      />
      <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-maple/10 text-maple mb-4">Chapter-Wise Practice</Badge>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-6">
                Master the Canadian Citizenship Test{" "}
                <span className="text-maple">Chapter by Chapter</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                The official "Discover Canada" guide has 11 chapters covering everything from Canadian history to government structure. 
                Our chapter-wise practice lets you focus on one topic at a time, ensuring you thoroughly understand each section before your citizenship test.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-maple hover:bg-maple-600 text-white rounded-full px-8" data-testid="hero-cta">
                    Start Practicing Free
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>500+ Questions</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>All 11 Chapters</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Detailed Explanations</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760469476196-f1193be67199?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjYW5hZGlhbiUyMGNpdGl6ZW5zaGlwJTIwY2FuYWRhJTIwZmxhZyUyMG1hcGxlJTIwbGVhZiUyMHBhdHJpb3RpY3xlbnwwfHx8fDE3NzE1MzMzNTB8MA&ixlib=rb-4.1.0&q=85" 
                alt="Canadian Flag - Citizenship Test Preparation"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">High Pass Rate</p>
                    <p className="text-sm text-slate-500">With chapter practice</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter List Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              All 11 Chapters from Discover Canada
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our question bank covers every topic tested on the Canadian Citizenship Test, organized by chapter for focused study.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chapters.map((chapter, index) => (
              <Card key={chapter.id} className="border border-slate-200 hover:border-maple/50 hover:shadow-md transition-all" data-testid={`chapter-card-${index + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-maple/10 rounded-lg flex items-center justify-center text-maple font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{chapter.title}</h3>
                        <p className="text-sm text-slate-500">{chapter.questions} questions</p>
                      </div>
                    </div>
                    <BookOpen className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Why Chapter-Wise Practice Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Breaking down the Discover Canada guide into manageable sections helps you retain information better and identify weak areas faster.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-white border border-slate-200" data-testid={`benefit-card-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-maple/10 rounded-xl flex items-center justify-center text-maple mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              How to Prepare for the Canadian Citizenship Test with Chapter Practice
            </h2>
            <p className="text-slate-600 mb-4">
              The Canadian Citizenship Test is based on the official study guide "Discover Canada: The Rights and Responsibilities of Citizenship." 
              This comprehensive guide covers Canadian history, government, geography, economy, and the rights and responsibilities that come with being a Canadian citizen.
            </p>
            <p className="text-slate-600 mb-4">
              Our chapter-wise practice system breaks down this 68-page guide into 11 manageable sections, allowing you to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Focus on one topic at a time without feeling overwhelmed</li>
              <li>Identify which chapters need more study time</li>
              <li>Build a strong foundation before attempting full mock tests</li>
              <li>Review specific sections when your test date approaches</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Whether you're preparing for your citizenship test in Toronto, Vancouver, Calgary, Montreal, or anywhere else in Canada, 
              our chapter-wise practice questions ensure you're ready for the 20 questions on your official test.
            </p>

            <h3 className="font-heading font-semibold text-xl text-slate-900 mt-8 mb-4">
              What to Expect on the Canadian Citizenship Test
            </h3>
            <p className="text-slate-600 mb-4">
              The citizenship test consists of 20 multiple-choice questions. You need to answer at least 15 correctly (75%) to pass. 
              Questions are drawn from all chapters of Discover Canada, covering:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Canadian history (Indigenous peoples, Confederation, World Wars)</li>
              <li>Canadian government (federal, provincial, municipal structures)</li>
              <li>Rights and responsibilities of citizenship</li>
              <li>Canadian symbols, economy, and geography</li>
              <li>Democratic elections and the justice system</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-maple">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Ready to Start Your Chapter-Wise Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of successful new Canadians who used our platform to pass their citizenship test.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-maple hover:bg-slate-100 rounded-full px-10" data-testid="cta-final">
              Start Free Trial
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
};

export default ChapterPracticePage;


